import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { InlineEdit } from '../components/InlineEdit';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  Export,
  FileCsv,
  FileXls,
  FilePdf,
  Broom,
  ClockCounterClockwise,
  ArrowCounterClockwise,
  Lightning,
  Wrench,
  Elevator,
  Certificate,
  FireSimple,
  Shield,
  ShieldCheck,
  ShieldWarning,
  Warning,
  Phone,
  EnvelopeSimple,
  User,
  DotsThreeVertical,
  ListChecks,
  Gear,
  CaretRight,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DatePickerDMY from '../components/DatePickerDMY';
// Phase 350 — BuildingsSettingsPanel moved to its own page at /building-settings.

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function Buildings() {
  const { t, language } = useLanguage();
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  // Phase 348 — `companyCounts` state removed.
  
  // Building dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Phase 214 — Pre-fetched data summary so the delete dialog can tell
  // the user exactly what will happen (archive vs hard delete) and which
  // collections hold data. Also drives the "Permanent Delete" flow on
  // already-archived buildings.
  const [deleteSummary, setDeleteSummary] = useState(null);
  const [deleteMode, setDeleteMode] = useState('soft'); // 'soft' | 'permanent'
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [deletingBuilding, setDeletingBuilding] = useState(null);
  
  // Unpaid detail modal
  const [unpaidModal, setUnpaidModal] = useState({ open: false, buildingId: '', buildingName: '', group: '', groupLabel: '' });
  const [unpaidDetails, setUnpaidDetails] = useState(null);
  const [unpaidLoading, setUnpaidLoading] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    building_code: '',
    location: '', 
    area: '',
    // Phase 235 — Structured address fields (matches Settings/Profile schema)
    street_address: '',
    zip_code: '',
    city: '',
    country: '',
    building_type: 'managed',
    takeover_date: '',
    managers: [],
    management_fee: 0,
    cleaning_fee: 0,
    cleaning_charge_mode: 'per_cleaning',
    management_vat_rate: 19,
    cleaning_vat_rate: 19,
    notes: '' 
  });
  
  // Cleaning History dialog
  const [cleaningDialogOpen, setCleaningDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [cleaningHistory, setCleaningHistory] = useState([]);
  const [cleaningFormData, setCleaningFormData] = useState({
    building_id: '',
    date: new Date().toISOString().split('T')[0],
    employee_name: '',
    cleaning_type: 'general_cleaning',
    work_types: ['general_cleaning'],
    other_notes: '',
    notes: ''
  });
  const [cleanersList, setCleanersList] = useState([]);
  const [cleaningTypes, setCleaningTypes] = useState([]);
  const [addingNewCleanerInBldg, setAddingNewCleanerInBldg] = useState(false);
  const [newCleanerNameInBldg, setNewCleanerNameInBldg] = useState('');

  // Insurance dialog
  const [insuranceDialogOpen, setInsuranceDialogOpen] = useState(false);
  const [insuranceHistoryDialogOpen, setInsuranceHistoryDialogOpen] = useState(false);
  const [insuranceList, setInsuranceList] = useState([]);
  const [editingInsurance, setEditingInsurance] = useState(null);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [addNewCompanyInline, setAddNewCompanyInline] = useState(false);
  const [insuranceDetailDialogOpen, setInsuranceDetailDialogOpen] = useState(false);
  const [viewingInsurance, setViewingInsurance] = useState(null);
  const [insuranceFormData, setInsuranceFormData] = useState({
    building_id: '',
    company_name: '',
    insurance_company_id: '',
    policy_number: '',
    policy_start_date: new Date().toISOString().split('T')[0],
    policy_end_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchBuildings();
    fetchInsuranceCompanies();
    // Phase 348 — `fetchCompanyCounts` removed; the counter strip it fed
    // was removed from this page.
  }, [filterType, filterStatus]);

  // Phase 348 — `fetchCompanyCounts` / `companyCounts` state removed (the
  // counter strip is gone).

  const fetchInsuranceCompanies = async () => {
    try {
      const res = await axios.get(`${API}/insurance-companies`, { withCredentials: true });
      setInsuranceCompanies(res.data);
    } catch { /* silent */ }
  };

  const handleInlineBuildingUpdate = async (buildingId, field, value) => {
    try {
      await axios.patch(`${API}/buildings/${buildingId}`, { [field]: value }, { withCredentials: true });
      toast.success(t('changesSaved'));
      fetchBuildings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const fetchBuildings = async () => {
    try {
      let url = `${API}/buildings`;
      const params = new URLSearchParams();
      if (filterType !== 'all') {
        params.append('building_type', filterType);
      }
      if (filterStatus !== 'all') {
        params.append('building_status', filterStatus);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      const [res, cleanersRes, typesRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/cleaners`),
        axios.get(`${API}/cleaning-types`),
      ]);
      setBuildings(res.data);
      setCleanersList(cleanersRes.data);
      setCleaningTypes(typesRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCleaningHistory = async (buildingId) => {
    try {
      const res = await axios.get(`${API}/cleaning-history?building_id=${buildingId}`);
      setCleaningHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch cleaning history:', error);
    }
  };

  const fetchInsuranceHistory = async (buildingId) => {
    try {
      const res = await axios.get(`${API}/insurance?building_id=${buildingId}`);
      setInsuranceList(res.data);
    } catch (error) {
      console.error('Failed to fetch insurance:', error);
    }
  };

  const openUnpaidDetail = async (buildingId, buildingName, group, groupLabel) => {
    setUnpaidModal({ open: true, buildingId, buildingName, group, groupLabel });
    setUnpaidLoading(true);
    setUnpaidDetails(null);
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/unpaid-details?group=${group}`);
      setUnpaidDetails(res.data);
    } catch (err) {
      toast.error(t('errorOccurred'));
    } finally {
      setUnpaidLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBuilding) {
        await axios.put(`${API}/buildings/${editingBuilding.building_id}`, formData);
        toast.success(t('savedSuccess'));
      } else {
        await axios.post(`${API}/buildings`, formData);
        toast.success(t('savedSuccess'));
      }
      setDialogOpen(false);
      setEditingBuilding(null);
      setFormData({ name: '', building_code: '', location: '', area: '', street_address: '', zip_code: '', city: '', country: '', building_type: 'managed', takeover_date: '', managers: [], management_fee: 0, cleaning_fee: 0, cleaning_charge_mode: 'per_cleaning', management_vat_rate: 19, cleaning_vat_rate: 19, notes: '' });
      fetchBuildings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      // Phase 214 — Pass `force=true` only for permanent delete on
      // already-archived buildings. The server enforces admin auth and
      // also rejects force=true on non-archived buildings, so non-admins
      // cannot bypass Archive even by tampering with this call.
      const url = `${API}/buildings/${deletingBuilding.building_id}` + (deleteMode === 'permanent' ? '?force=true' : '');
      const res = await axios.delete(url);
      if (res.data.permanent) {
        toast.success(t('buildingPermanentlyDeleted') || 'Building permanently deleted');
      } else if (res.data.archived) {
        toast.success(t('buildingArchived') || 'Building archived (has historical data)');
      } else {
        toast.success(t('deletedSuccess'));
      }
      setDeleteDialogOpen(false);
      setDeletingBuilding(null);
      setDeleteSummary(null);
      setDeleteMode('soft');
      fetchBuildings();
      // Phase 831 — Signal every other tab / page (Dashboard, etc.)
      // to refetch its cached data. The Dashboard listens for this
      // key via a `storage` event and via visibility change.
      try {
        window.localStorage.setItem('dashboard:invalidate', String(Date.now()));
      } catch (_) { /* localStorage may be blocked */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  // Phase 214 — Open the delete dialog with a pre-loaded data summary so
  // the confirmation copy can show counts and the right action label.
  const openDeleteDialog = async (building, mode = 'soft') => {
    setDeletingBuilding(building);
    setDeleteMode(mode);
    setDeleteSummary(null);
    setDeleteDialogOpen(true);
    try {
      const res = await axios.get(`${API}/buildings/${building.building_id}/data-summary`);
      setDeleteSummary(res.data);
    } catch {
      // Non-fatal: dialog still works, just without counts.
      setDeleteSummary({ has_data: false, counts: {} });
    }
  };

  const handleRestore = async (building) => {
    try {
      await axios.put(`${API}/buildings/${building.building_id}/restore`);
      toast.success(t('buildingRestored') || 'Building restored');
      fetchBuildings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleCleaningSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cleaning-history`, cleaningFormData);
      toast.success(t('savedSuccess'));
      setCleaningDialogOpen(false);
      resetCleaningForm();
      fetchBuildings();
      if (selectedBuilding) {
        fetchCleaningHistory(selectedBuilding.building_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleQuickClean = async (building) => {
    try {
      await axios.post(`${API}/cleaning-history/quick-add?building_id=${building.building_id}`);
      toast.success(t('cleanedToday'));
      fetchBuildings();
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const handleUpdateLastCleaning = async (buildingId, date) => {
    try {
      await axios.put(`${API}/buildings/${buildingId}/last-cleaning`, { date });
      fetchBuildings();
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const handleInsuranceSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...insuranceFormData };
      if (editingInsurance) {
        await axios.put(`${API}/insurance/${editingInsurance.insurance_id}`, {
          company_name: payload.company_name,
          insurance_company_id: payload.insurance_company_id,
          policy_number: payload.policy_number,
          policy_start_date: payload.policy_start_date,
          policy_end_date: payload.policy_end_date,
          notes: payload.notes
        });
      } else {
        await axios.post(`${API}/insurance`, payload);
      }
      toast.success(t('savedSuccess'));
      setInsuranceDialogOpen(false);
      setEditingInsurance(null);
      setAddNewCompanyInline(false);
      resetInsuranceForm();
      fetchBuildings();
      if (selectedBuilding) {
        fetchInsuranceHistory(selectedBuilding.building_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDeleteInsurance = async (insuranceId) => {
    try {
      await axios.delete(`${API}/insurance/${insuranceId}`);
      toast.success(t('deletedSuccess'));
      fetchBuildings();
      if (selectedBuilding) {
        fetchInsuranceHistory(selectedBuilding.building_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const resetCleaningForm = () => {
    setCleaningFormData({
      building_id: '',
      date: new Date().toISOString().split('T')[0],
      employee_name: user?.name || '',
      cleaning_type: 'general_cleaning',
      work_types: ['general_cleaning'],
      other_notes: '',
      notes: ''
    });
    setAddingNewCleanerInBldg(false);
    setNewCleanerNameInBldg('');
  };

  const handleInlineAddCleanerInBldg = async () => {
    if (!newCleanerNameInBldg.trim()) return;
    try {
      await axios.post(`${API}/cleaners`, { name: newCleanerNameInBldg.trim() });
      setCleaningFormData({ ...cleaningFormData, employee_name: newCleanerNameInBldg.trim() });
      setAddingNewCleanerInBldg(false);
      setNewCleanerNameInBldg('');
      const res = await axios.get(`${API}/cleaners`);
      setCleanersList(res.data);
      toast.success(t('savedSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const resetInsuranceForm = () => {
    setInsuranceFormData({
      building_id: '',
      company_name: '',
      insurance_company_id: '',
      policy_number: '',
      policy_start_date: new Date().toISOString().split('T')[0],
      policy_end_date: '',
      notes: ''
    });
    setAddNewCompanyInline(false);
  };

  const openEditDialog = (building) => {
    setEditingBuilding(building);
    setFormData({
      name: building.name,
      building_code: building.building_code || '',
      location: building.location,
      area: building.area || '',
      // Phase 235 — Migrate existing data: if structured fields are empty,
      // fall back to legacy `location` so the user sees their old value
      // pre-filled and can split it manually on first edit.
      street_address: building.street_address || building.location || '',
      zip_code: building.zip_code || '',
      city: building.city || '',
      country: building.country || '',
      building_type: building.building_type || 'managed',
      takeover_date: building.takeover_date || '',
      managers: building.managers || [],
      management_fee: building.management_fee || 0,
      cleaning_fee: building.cleaning_fee || 0,
      cleaning_charge_mode: building.cleaning_charge_mode || 'per_cleaning',
      management_vat_rate: building.management_vat_rate ?? building.vat_rate ?? 19,
      cleaning_vat_rate: building.cleaning_vat_rate ?? building.vat_rate ?? 19,
      notes: building.notes || ''
    });
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingBuilding(null);
    setFormData({ 
      name: '', 
      building_code: '',
      location: '', 
      area: '',
      street_address: '',
      zip_code: '',
      city: '',
      country: '',
      building_type: 'managed', 
      takeover_date: '',
      managers: [],
      management_fee: 0,
      cleaning_fee: 0,
      cleaning_charge_mode: 'per_cleaning',
      management_vat_rate: 19,
      cleaning_vat_rate: 19,
      notes: '' 
    });
    setDialogOpen(true);
  };

  const openCleaningDialog = (building) => {
    setSelectedBuilding(building);
    setCleaningFormData({
      ...cleaningFormData,
      building_id: building.building_id,
      employee_name: user?.name || ''
    });
    setCleaningDialogOpen(true);
  };

  const openHistoryDialog = async (building) => {
    setSelectedBuilding(building);
    await fetchCleaningHistory(building.building_id);
    setHistoryDialogOpen(true);
  };

  const openInsuranceDialog = (building, insurance = null) => {
    setSelectedBuilding(building);
    setEditingInsurance(insurance);
    setAddNewCompanyInline(false);
    if (insurance) {
      setInsuranceFormData({
        building_id: building.building_id,
        company_name: insurance.company_name,
        insurance_company_id: insurance.insurance_company_id || '',
        policy_number: insurance.policy_number || '',
        policy_start_date: insurance.policy_start_date,
        policy_end_date: insurance.policy_end_date,
        notes: insurance.notes || ''
      });
    } else {
      setInsuranceFormData({
        building_id: building.building_id,
        company_name: '',
        insurance_company_id: '',
        policy_number: '',
        policy_start_date: new Date().toISOString().split('T')[0],
        policy_end_date: '',
        notes: ''
      });
    }
    setInsuranceDialogOpen(true);
  };

  const openInsuranceHistoryDialog = async (building) => {
    setSelectedBuilding(building);
    await fetchInsuranceHistory(building.building_id);
    setInsuranceHistoryDialogOpen(true);
  };

  const openInsuranceDetail = async (building) => {
    if (!building.insurance_id) return;
    try {
      const res = await axios.get(`${API}/insurance/${building.insurance_id}`, { withCredentials: true });
      setViewingInsurance(res.data);
      setSelectedBuilding(building);
      setInsuranceDetailDialogOpen(true);
    } catch {
      // fallback: open history
      openInsuranceHistoryDialog(building);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'pending': return 'status-pending';
      case 'upcoming': return 'status-upcoming';
      default: return 'status-pending';
    }
  };

  const getCleaningStatus = (daysSince) => {
    if (daysSince === null) return 'text-zinc-400';
    if (daysSince === 0) return 'text-emerald-600';
    if (daysSince <= 7) return 'text-emerald-600';
    if (daysSince <= 14) return 'text-amber-600';
    return 'text-red-600';
  };

  const getInsuranceStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800 border-emerald-400';
      case 'expiring_soon':
        return 'bg-amber-100 text-amber-800 border-amber-400';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-400';
      default:
        return 'bg-zinc-100 text-zinc-600 border-zinc-300';
    }
  };

  const getInsuranceIcon = (status) => {
    switch (status) {
      case 'active':
        return <ShieldCheck size={16} weight="fill" className="text-emerald-600" />;
      case 'expiring_soon':
        return <ShieldWarning size={16} weight="fill" className="text-amber-600" />;
      case 'expired':
        return <Warning size={16} weight="fill" className="text-red-600" />;
      default:
        return <Shield size={16} className="text-zinc-400" />;
    }
  };

  const getDisplayAddress = (b) => [b.street_address, b.zip_code, b.city, b.country].filter(Boolean).join(', ') || b.location || '';

  const filteredBuildings = buildings.filter(b =>
    b.name.toLowerCase().includes(search.toLowerCase()) ||
    getDisplayAddress(b).toLowerCase().includes(search.toLowerCase())
  );

  // Export functions
  const exportToCSV = () => {
    const data = filteredBuildings.map(b => ({
      [t('buildingName')]: b.name,
      [t('location')]: getDisplayAddress(b),
      [t('buildingType')]: t(b.building_type),
      [t('lastCleaning')]: b.last_cleaning_date || '-',
      [t('insuranceCompany')]: b.insurance_company || '-',
      [t('insuranceStatus')]: b.insurance_status ? t(b.insurance_status) : '-',
      [t('policyEndDate')]: b.insurance_end_date || '-',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'buildings.csv');
  };

  const exportToExcel = () => {
    const data = filteredBuildings.map(b => ({
      [t('buildingName')]: b.name,
      [t('location')]: getDisplayAddress(b),
      [t('buildingType')]: t(b.building_type),
      [t('lastCleaning')]: b.last_cleaning_date || '-',
      [t('insuranceCompany')]: b.insurance_company || '-',
      [t('insuranceStatus')]: b.insurance_status ? t(b.insurance_status) : '-',
      [t('policyEndDate')]: b.insurance_end_date || '-',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Buildings');
    XLSX.writeFile(wb, 'buildings.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text(t('buildings'), 14, 15);
    autoTable(doc, {
      startY: 25,
      head: [[t('buildingName'), t('location'), t('buildingType'), t('lastCleaning'), t('insuranceStatus')]],
      body: filteredBuildings.map(b => [
        b.name, 
        getDisplayAddress(b), 
        t(b.building_type), 
        b.last_cleaning_date || '-', 
        b.insurance_status ? t(b.insurance_status) : '-'
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [24, 24, 27] }
    });
    doc.save('buildings.pdf');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">{t('loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="buildings-page">
        {/* Phase 348 — Companies overview strip removed; the four catalogues
            (Insurance / Elevator / Certification / Fire) plus Vendors live
            inside Settings → Companies. */}

        {/* Phase 350 — Settings extracted to its own page (/building-settings).
            The /buildings list now shows a compact entry button instead of an
            inline collapsible drawer so the table is the focus of this view. */}
        {isAdmin && (
          <button
            type="button"
            onClick={() => navigate('/building-settings')}
            className="bg-white border border-zinc-200 mb-4 w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors"
            data-testid="buildings-open-settings-btn"
          >
            <Gear size={16} weight="fill" className="text-zinc-600" />
            <div className="flex-1 text-left">
              <h2 className="font-bold text-sm" style={{ fontFamily: 'var(--font-heading)' }}>{t('settings') || 'Settings'}</h2>
              <p className="text-[11px] text-zinc-500 leading-none mt-0.5">Κοινά settings που ισχύουν σε όλες τις πολυκατοικίες</p>
            </div>
            <CaretRight size={14} className="text-zinc-400" />
          </button>
        )}

        {/* Header */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                type="text"
                placeholder={t('search') + '...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-zinc-200 rounded-none"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[160px] rounded-none" data-testid="type-filter">
                  <SelectValue placeholder={t('buildingType')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('all')}</SelectItem>
                  <SelectItem value="managed">{t('managed')}</SelectItem>
                  <SelectItem value="cleaning_only">{t('cleaning_only')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[160px] rounded-none" data-testid="status-filter">
                  <SelectValue placeholder={t('status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('all')}</SelectItem>
                  <SelectItem value="active">{t('active')}</SelectItem>
                  <SelectItem value="inactive">{t('inactive')}</SelectItem>
                  <SelectItem value="archived">{t('archived')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-between items-center flex-wrap gap-2">
            <span className="text-sm text-zinc-500">{filteredBuildings.length} {t('buildings')}</span>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-zinc-200 rounded-none" data-testid="export-btn">
                    <Export size={18} className="mr-2" />
                    {t('export')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportToCSV}><FileCsv size={18} className="mr-2" />CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToExcel}><FileXls size={18} className="mr-2" />Excel</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToPDF}><FilePdf size={18} className="mr-2" />PDF</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                onClick={() => navigate('/tasks')}
                variant="outline"
                className="border-zinc-200 rounded-none"
                data-testid="view-all-tasks-btn"
                title="Όλα τα tasks από όλα τα κτίρια"
              >
                <ListChecks size={18} className="mr-2" />
                {t('viewAllTasks')}
              </Button>
              {isAdmin && (
                <Button onClick={openAddDialog} className="bg-zinc-950 rounded-none" data-testid="add-building-btn">
                  <Plus size={18} className="mr-2" />
                  {t('addBuilding')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Table - Desktop */}
        <div className="hidden xl:block bg-white border border-zinc-200 overflow-x-auto">
          {filteredBuildings.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">{t('noBuildings')}</div>
          ) : (
            <table className="excel-table" data-testid="buildings-table">
              <thead>
                <tr>
                  {/* Phase 207k — Removed Manager column. Committee comes from latest meeting. */}
                  <th>{t('buildingCode')}</th>
                  <th>{t('buildingName')}</th>
                  <th className="text-center">{t('units')}</th>
                  <th>{t('address')}</th>
                  <th>{t('takeoverDate')}</th>
                  <th>{t('status')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredBuildings.map((building) => (
                  <tr key={building.building_id} data-testid={`building-row-${building.building_id}`} className={building.building_status === 'inactive' ? 'bg-zinc-50 opacity-75' : building.building_status === 'archived' ? 'bg-amber-50/50 opacity-60' : ''}>
                    <td className="font-mono text-xs text-zinc-500" data-testid={`building-code-${building.building_id}`}>
                      {building.building_code || '—'}
                    </td>
                    <td className="font-medium" data-testid={`building-name-cell-${building.building_id}`}>
                      <button
                        onClick={() => navigate(building.building_type === 'cleaning_only' ? `/buildings/${building.building_id}/statement` : `/buildings/${building.building_id}`)}
                        className="text-left hover:text-blue-700 hover:underline transition-colors"
                        data-testid={`building-link-${building.building_id}`}
                      >
                        {building.name}
                      </button>
                    </td>
                    <td className="text-center font-mono" data-testid={`building-units-${building.building_id}`}>
                      {building.units ?? 0}
                    </td>
                    {/* Phase 207k — Manager column removed. */}
                    <td className="text-zinc-600" data-testid={`building-address-${building.building_id}`}>
                      {[building.street_address, building.zip_code, building.city, building.country].filter(Boolean).join(', ') || building.location || '—'}
                    </td>
                    {/* Phase 162 — Takeover Date for active, Release Date for inactive */}
                    <td data-testid={`building-takeover-${building.building_id}`}>
                      {building.building_status === 'inactive' ? (
                        <div>
                          <span className="block text-[10px] uppercase tracking-wider text-red-500 font-semibold">{t('releaseDate')}</span>
                          <span className="font-mono text-sm" data-testid={`building-release-date-${building.building_id}`}>
                            {building.close_date ? formatDateDMY(building.close_date) : '—'}
                          </span>
                        </div>
                      ) : (
                        <InlineEdit
                          value={building.takeover_date || ''}
                          onSave={(val) => handleInlineBuildingUpdate(building.building_id, 'takeover_date', val)}
                          className="font-mono text-sm"
                          type="date"
                          placeholder="—"
                          displayValue={building.takeover_date ? formatDateDMY(building.takeover_date) : '—'}
                        />
                      )}
                    </td>
                    <td data-testid={`status-cell-${building.building_id}`}>
                      {building.building_status === 'archived' ? (
                        <span className="px-2 py-0.5 text-[10px] font-semibold uppercase bg-amber-50 text-amber-600 border border-amber-200">{t('archived')}</span>
                      ) : building.building_status === 'inactive' ? (
                        <span className="px-2 py-0.5 text-[10px] font-semibold uppercase bg-red-50 text-red-600 border border-red-200">{t('inactive')}</span>
                      ) : (
                        <span className="px-2 py-0.5 text-[10px] font-semibold uppercase bg-emerald-50 text-emerald-600 border border-emerald-200">{t('active')}</span>
                      )}
                    </td>
                    <td className="overflow-visible">
                      <div className="flex items-center gap-1">
                        {isAdmin && building.building_status === 'archived' ? (
                          <>
                            <button
                              onClick={() => handleRestore(building)}
                              className="p-1.5 hover:bg-emerald-50 text-emerald-600 transition-colors rounded text-xs font-medium"
                              title={t('restore')}
                              data-testid={`restore-building-${building.building_id}`}
                            >
                              <ArrowCounterClockwise size={18} />
                            </button>
                            {/* Phase 214 — Permanent Delete is only available
                                on already-archived buildings, behind a
                                strong confirmation that lists the data
                                that will be wiped. */}
                            <button
                              onClick={() => openDeleteDialog(building, 'permanent')}
                              className="p-1.5 hover:bg-red-50 text-red-600 transition-colors rounded"
                              title={t('permanentlyDelete') || 'Permanently Delete'}
                              data-testid={`permanent-delete-building-${building.building_id}`}
                            >
                              <Trash size={18} />
                            </button>
                          </>
                        ) : isAdmin && (
                          <>
                            <button
                              onClick={() => openEditDialog(building)}
                              className="p-1.5 hover:bg-zinc-100 transition-colors rounded"
                              title={t('edit')}
                              data-testid={`edit-building-${building.building_id}`}
                            >
                              <PencilSimple size={18} />
                            </button>
                            <button
                              onClick={() => openDeleteDialog(building, 'soft')}
                              className="p-1.5 hover:bg-red-50 text-red-600 transition-colors rounded"
                              title={t('delete')}
                              data-testid={`delete-building-${building.building_id}`}
                            >
                              <Trash size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Cards - Mobile/Tablet */}
        <div className="xl:hidden space-y-3">
          {filteredBuildings.length === 0 ? (
            <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">
              {t('noBuildings')}
            </div>
          ) : (
            filteredBuildings.map((building, idx) => (
              <div key={building.building_id} className={`mobile-card ${building.building_status === 'archived' ? 'opacity-60' : ''}`} data-testid={`building-card-${building.building_id}`}>
                <div className="mobile-card-header">
                  <div>
                    <h3 className="mobile-card-title">
                      <span className="text-xs text-zinc-400 mr-1">{idx + 1}.</span>
                      <button
                        onClick={() => navigate(building.building_type === 'cleaning_only' ? `/buildings/${building.building_id}/statement` : `/buildings/${building.building_id}`)}
                        className="text-left hover:text-blue-700 hover:underline transition-colors"
                        data-testid={`building-card-link-${building.building_id}`}
                      >
                        {building.name}
                      </button>
                    </h3>
                    <p className="text-sm text-zinc-500">{getDisplayAddress(building)}</p>
                    {/* Phase 207k — managers display removed (committee from meetings) */}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-2 py-1 text-xs font-mono uppercase border ${
                      building.building_type === 'managed' 
                        ? 'bg-blue-50 text-blue-700 border-blue-200' 
                        : 'bg-purple-50 text-purple-700 border-purple-200'
                    }`}>
                      {t(building.building_type || 'managed')}
                    </span>
                    {building.building_status === 'archived' && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-amber-50 text-amber-600 border border-amber-200">{t('archived')}</span>
                    )}
                    {building.building_status === 'inactive' && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-red-50 text-red-600 border border-red-200">{t('closed')}</span>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2 text-sm mt-3">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('buildingCode')}</span>
                    <span className="font-mono text-xs">{building.building_code || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('units')}</span>
                    <span className="font-mono">{building.units ?? 0}</span>
                  </div>
                  {building.building_type === 'managed' && building.management_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500">{t('managementFee')}</span>
                      <span className="font-mono text-xs">&euro;{building.management_fee?.toFixed(2)} <span className="text-zinc-400">+{building.management_vat_rate ?? 19}%</span></span>
                    </div>
                  )}
                  {building.cleaning_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500">{t('cleaningFee')}</span>
                      <span className="font-mono text-xs">&euro;{building.cleaning_fee?.toFixed(2)} <span className="text-zinc-400">+{building.cleaning_vat_rate ?? 19}%</span></span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('takeoverDate')}</span>
                    <span className="font-mono text-xs">{building.takeover_date ? formatDateDMY(building.takeover_date) : '—'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-500">{t('status')}</span>
                    {building.building_status === 'archived' ? (
                      <span className="px-2 py-0.5 text-[10px] font-semibold uppercase bg-amber-50 text-amber-600 border border-amber-200">{t('archived')}</span>
                    ) : building.building_status === 'inactive' ? (
                      <span className="px-2 py-0.5 text-[10px] font-semibold uppercase bg-red-50 text-red-600 border border-red-200">{t('inactive')}</span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] font-semibold uppercase bg-emerald-50 text-emerald-600 border border-emerald-200">{t('active')}</span>
                    )}
                  </div>
                </div>
                
                {/* Mobile Quick Actions */}
                <div className="flex gap-2 mt-4 pt-4 border-t border-zinc-100">
                  <Button
                    onClick={() => navigate(building.building_type === 'cleaning_only' ? `/buildings/${building.building_id}/statement` : `/buildings/${building.building_id}`)}
                    variant="outline"
                    className="flex-1 rounded-none"
                    data-testid={`mobile-view-${building.building_id}`}
                  >
                    {building.building_type === 'cleaning_only' ? t('buildingStatement') : t('viewDetails')}
                  </Button>
                  {isAdmin && building.building_status === 'archived' ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => handleRestore(building)}
                        className="rounded-none text-emerald-600 hover:bg-emerald-50"
                        data-testid={`mobile-restore-${building.building_id}`}
                      >
                        <ArrowCounterClockwise size={16} />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => openDeleteDialog(building, 'permanent')}
                        className="rounded-none text-red-600 hover:bg-red-50"
                        data-testid={`mobile-permanent-delete-${building.building_id}`}
                      >
                        <Trash size={16} />
                      </Button>
                    </>
                  ) : isAdmin && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => openEditDialog(building)}
                        className="rounded-none"
                        data-testid={`mobile-edit-${building.building_id}`}
                      >
                        <PencilSimple size={16} />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => openDeleteDialog(building, 'soft')}
                        className="rounded-none text-red-600 hover:bg-red-50"
                        data-testid={`mobile-delete-${building.building_id}`}
                      >
                        <Trash size={16} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Building Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {editingBuilding ? t('editBuilding') : t('addBuilding')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{t('buildingName')}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 rounded-none"
                    required
                    data-testid="building-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="building_code">{t('buildingCode')}</Label>
                  <Input
                    id="building_code"
                    value={formData.building_code}
                    onChange={(e) => setFormData({ ...formData, building_code: e.target.value })}
                    className="mt-1 rounded-none font-mono"
                    placeholder={t('autoGenerated')}
                    data-testid="building-code-input"
                  />
                </div>
              </div>
              {/* Phase 235 — Structured address (Street / ZIP / City / Country)
                  matching the Settings → Profile schema. Existing buildings
                  with a single `location` value get it pre-filled into
                  Street Address on first edit. */}
              <div>
                <Label htmlFor="street_address">{t('streetAddress') || 'Street Address'}</Label>
                <Input
                  id="street_address"
                  value={formData.street_address || ''}
                  onChange={(e) => setFormData({ ...formData, street_address: e.target.value, location: e.target.value })}
                  className="mt-1 rounded-none"
                  data-testid="building-street-address-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="zip_code">{t('zipCode') || 'ZIP Code'}</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code || ''}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    className="mt-1 rounded-none font-mono"
                    data-testid="building-zip-code-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city">{t('city') || 'City'}</Label>
                  <Input
                    id="city"
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="mt-1 rounded-none"
                    data-testid="building-city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="country">{t('countryRegion') || t('country') || 'Country / Region'}</Label>
                  <Input
                    id="country"
                    value={formData.country || ''}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="mt-1 rounded-none"
                    data-testid="building-country-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="area">{t('area') || 'Area'}</Label>
                <Input
                  id="area"
                  value={formData.area || ''}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                  className="mt-1 rounded-none"
                  data-testid="building-area-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('buildingType')}</Label>
                  <Select
                    value={formData.building_type}
                    onValueChange={(val) => setFormData({ ...formData, building_type: val, management_fee: val === 'cleaning_only' ? 0 : formData.management_fee })}
                  >
                    <SelectTrigger className="mt-1 rounded-none" data-testid="building-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="managed">{t('managed')}</SelectItem>
                      <SelectItem value="cleaning_only">{t('cleaning_only')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="takeover_date">{t('takeoverDate')}</Label>
                  <DatePickerDMY
                    value={formData.takeover_date}
                    onChange={(v) => setFormData({ ...formData, takeover_date: v })}
                    className="mt-1 font-mono"
                    data-testid="building-takeover-date-input"
                  />
                </div>
              </div>
              
              {/* Phase 207i — Managers & Financials sections REMOVED from this
                  dialog. Managers are managed via the Building Details page
                  (separate "Διαχειριστική Επιτροπή" section). Financial fee
                  fields (management_fee, cleaning_fee, VAT rates) are no longer
                  exposed in the UI but the data stays in the DB to avoid
                  breaking existing invoice rules. */}

              <div>
                <Label htmlFor="notes">{t('notes')}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="mt-1 rounded-none"
                  rows={2}
                  data-testid="building-notes-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-building-btn">
                  {t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Cleaning Entry Dialog */}
        <Dialog open={cleaningDialogOpen} onOpenChange={setCleaningDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('logCleaning')} - {selectedBuilding?.name}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCleaningSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="clean_date">{t('date')}</Label>
                  <DatePickerDMY
                    value={cleaningFormData.date}
                    onChange={(v) => setCleaningFormData({ ...cleaningFormData, date: v })}
                    className="mt-1 font-mono"
                    data-testid="cleaning-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="employee">{t('employeeName')}</Label>
                  <Input
                    id="employee"
                    value={cleaningFormData.employee_name}
                    onChange={(e) => setCleaningFormData({ ...cleaningFormData, employee_name: e.target.value })}
                    className="mt-1 rounded-none"
                    data-testid="employee-name-input"
                  />
                </div>
              </div>
              
              <div>
                <Label>{t('cleaningType')}</Label>
                <Select value={cleaningFormData.cleaning_type} onValueChange={(val) => setCleaningFormData({ ...cleaningFormData, cleaning_type: val, work_types: [val] })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="cleaning-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cleaningTypes.map((ct) => (
                      <SelectItem key={ct.key} value={ct.key}>{language === 'el' ? (ct.name_el || ct.name_en) : ct.name_en}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {cleaningFormData.cleaning_type === 'other' && (
                <div>
                  <Label htmlFor="other_notes">{t('otherNotes')}</Label>
                  <Input
                    id="other_notes"
                    value={cleaningFormData.other_notes}
                    onChange={(e) => setCleaningFormData({ ...cleaningFormData, other_notes: e.target.value })}
                    className="mt-1 rounded-none"
                    placeholder={t('otherNotes')}
                    data-testid="other-notes-input"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="clean_notes">{t('notes')}</Label>
                <Textarea
                  id="clean_notes"
                  value={cleaningFormData.notes}
                  onChange={(e) => setCleaningFormData({ ...cleaningFormData, notes: e.target.value })}
                  className="mt-1 rounded-none"
                  rows={2}
                  data-testid="cleaning-notes-input"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCleaningDialogOpen(false)} className="rounded-none">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 rounded-none" data-testid="save-cleaning-btn">
                  <Broom size={18} className="mr-2" />
                  {t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Cleaning History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('cleaningHistory')} - {selectedBuilding?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {cleaningHistory.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">{t('noCleaning')}</div>
              ) : (
                cleaningHistory.map((entry) => (
                  <div key={entry.entry_id} className="border border-zinc-200 p-4" data-testid={`history-entry-${entry.entry_id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-mono text-sm font-semibold">{entry.date}</span>
                        <span className="mx-2 text-zinc-300">|</span>
                        <span className="text-zinc-600">{entry.employee_name}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {entry.work_types.map((type) => {
                        const ct = cleaningTypes.find(c => c.key === type);
                        const label = ct ? (language === 'el' ? (ct.name_el || ct.name_en) : ct.name_en) : t(type);
                        return (
                          <span key={type} className="px-2 py-0.5 bg-zinc-100 text-zinc-700 text-xs border border-zinc-200">
                            {label}
                          </span>
                        );
                      })}
                    </div>
                    {entry.notes && (
                      <p className="text-sm text-zinc-500">{entry.notes}</p>
                    )}
                    {entry.other_notes && (
                      <p className="text-sm text-zinc-500 italic">{entry.other_notes}</p>
                    )}
                  </div>
                ))
              )}
            </div>
            <DialogFooter>
              <Button 
                onClick={() => {
                  setHistoryDialogOpen(false);
                  openCleaningDialog(selectedBuilding);
                }}
                className="bg-emerald-600 hover:bg-emerald-700 rounded-none"
              >
                <Plus size={18} className="mr-2" />
                {t('addCleaningEntry')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Insurance Dialog */}
        <Dialog open={insuranceDialogOpen} onOpenChange={setInsuranceDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {editingInsurance ? t('editInsurance') : t('addInsurance')} - {selectedBuilding?.name}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInsuranceSubmit} className="space-y-4">
              {/* Company Selection */}
              <div>
                <Label htmlFor="insurance_company">{t('insuranceCompany')}</Label>
                {!addNewCompanyInline ? (
                  <div className="flex gap-2 mt-1">
                    <Select
                      value={insuranceFormData.insurance_company_id || '__manual__'}
                      onValueChange={(val) => {
                        if (val === '__manual__') {
                          setInsuranceFormData({ ...insuranceFormData, insurance_company_id: '', company_name: '' });
                        } else {
                          const co = insuranceCompanies.find(c => c.company_id === val);
                          setInsuranceFormData({ ...insuranceFormData, insurance_company_id: val, company_name: co?.name || '' });
                        }
                      }}
                    >
                      <SelectTrigger className="flex-1 rounded-none" data-testid="insurance-company-select">
                        <SelectValue placeholder={t('selectCompany')} />
                      </SelectTrigger>
                      <SelectContent>
                        {insuranceCompanies.map(co => (
                          <SelectItem key={co.company_id} value={co.company_id}>{co.name}</SelectItem>
                        ))}
                        <SelectItem value="__manual__">{t('typeManually')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" className="rounded-none" size="sm" onClick={() => setAddNewCompanyInline(true)} data-testid="add-new-company-inline-btn">
                      <Plus size={16} />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2 mt-1 p-3 bg-zinc-50 border border-zinc-200">
                    <p className="text-xs font-medium text-zinc-500">{t('addInsuranceCompany')}</p>
                    <Input
                      placeholder={t('companyName')}
                      value={insuranceFormData.company_name}
                      onChange={(e) => setInsuranceFormData({ ...insuranceFormData, company_name: e.target.value })}
                      className="rounded-none"
                      required
                      data-testid="new-company-name-input"
                    />
                    <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={() => setAddNewCompanyInline(false)} data-testid="cancel-new-company-btn">
                      {t('cancel')}
                    </Button>
                  </div>
                )}
                {!addNewCompanyInline && !insuranceFormData.insurance_company_id && (
                  <Input
                    value={insuranceFormData.company_name}
                    onChange={(e) => setInsuranceFormData({ ...insuranceFormData, company_name: e.target.value })}
                    className="mt-2 rounded-none"
                    placeholder={t('companyName')}
                    required
                    data-testid="insurance-company-input"
                  />
                )}
              </div>

              {/* Auto-filled contact info from company */}
              {insuranceFormData.insurance_company_id && (() => {
                const co = insuranceCompanies.find(c => c.company_id === insuranceFormData.insurance_company_id);
                if (!co) return null;
                return (co.phone || co.email) ? (
                  <div className="bg-blue-50 border border-blue-200 p-3 space-y-1" data-testid="auto-contact-info">
                    <p className="text-xs font-semibold text-blue-700 uppercase">{t('contactInfo')}</p>
                    {co.phone && <a href={`tel:${co.phone}`} className="flex items-center gap-2 text-sm text-blue-700 hover:underline"><Phone size={14} />{co.phone}</a>}
                    {co.email && <a href={`mailto:${co.email}`} className="flex items-center gap-2 text-sm text-blue-700 hover:underline"><EnvelopeSimple size={14} />{co.email}</a>}
                    {co.contact_person && <div className="flex items-center gap-2 text-xs text-blue-600"><User size={14} />{co.contact_person}</div>}
                  </div>
                ) : null;
              })()}

              {/* Policy Number */}
              <div>
                <Label htmlFor="policy_number">{t('policyNumber')}</Label>
                <Input
                  id="policy_number"
                  value={insuranceFormData.policy_number}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, policy_number: e.target.value })}
                  className="mt-1 rounded-none font-mono"
                  placeholder="e.g. POL-2026-001"
                  data-testid="policy-number-input"
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="policy_start">{t('policyStartDate')}</Label>
                  <DatePickerDMY
                    value={insuranceFormData.policy_start_date}
                    onChange={(v) => setInsuranceFormData({ ...insuranceFormData, policy_start_date: v })}
                    className="mt-1 font-mono"
                    data-testid="policy-start-input"
                  />
                </div>
                <div>
                  <Label htmlFor="policy_end">{t('policyEndDate')}</Label>
                  <DatePickerDMY
                    value={insuranceFormData.policy_end_date}
                    onChange={(v) => setInsuranceFormData({ ...insuranceFormData, policy_end_date: v })}
                    className="mt-1 font-mono"
                    data-testid="policy-end-input"
                  />
                </div>
              </div>
              
              {insuranceFormData.policy_start_date && insuranceFormData.policy_end_date && (
                <div className="bg-zinc-50 border border-zinc-200 p-3">
                  <span className="text-sm text-zinc-600">{t('coveragePeriod')}: </span>
                  <span className="font-mono font-semibold">
                    {Math.max(1, Math.round((new Date(insuranceFormData.policy_end_date) - new Date(insuranceFormData.policy_start_date)) / (1000 * 60 * 60 * 24 * 30)))} {t('months')}
                  </span>
                </div>
              )}

              <div>
                <Label htmlFor="insurance_notes">{t('notes')}</Label>
                <Textarea
                  id="insurance_notes"
                  value={insuranceFormData.notes}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, notes: e.target.value })}
                  className="mt-1 rounded-none"
                  rows={2}
                  data-testid="insurance-notes-input"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInsuranceDialogOpen(false)} className="rounded-none">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700 rounded-none" data-testid="save-insurance-btn">
                  <Shield size={18} className="mr-2" />
                  {t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Insurance Detail Dialog */}
        <Dialog open={insuranceDetailDialogOpen} onOpenChange={setInsuranceDetailDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('insuranceDetails')} - {selectedBuilding?.name}
              </DialogTitle>
            </DialogHeader>
            {viewingInsurance && (
              <div className="space-y-4">
                {/* Status badge */}
                <div className="flex items-center gap-3">
                  {getInsuranceIcon(viewingInsurance.status)}
                  <span className={`px-3 py-1 text-sm font-mono uppercase border ${getInsuranceStatusBadge(viewingInsurance.status)}`}>
                    {t(viewingInsurance.status)}
                  </span>
                  <span className={`text-sm font-mono ${
                    viewingInsurance.days_until_expiry < 0 ? 'text-red-600' : viewingInsurance.days_until_expiry <= 30 ? 'text-amber-600' : 'text-emerald-600'
                  }`}>
                    {viewingInsurance.days_until_expiry < 0
                      ? `${t('expiredDaysAgo')} ${Math.abs(viewingInsurance.days_until_expiry)} ${t('days')}`
                      : `${t('expiresIn')} ${viewingInsurance.days_until_expiry} ${t('days')}`
                    }
                  </span>
                </div>

                {/* Company & Policy Info */}
                <div className="bg-zinc-50 border border-zinc-200 p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-zinc-500">{t('insuranceCompany')}</span>
                    <span className="text-sm font-medium">{viewingInsurance.company_name}</span>
                  </div>
                  {viewingInsurance.policy_number && (
                    <div className="flex justify-between">
                      <span className="text-sm text-zinc-500">{t('policyNumber')}</span>
                      <span className="text-sm font-mono font-medium">{viewingInsurance.policy_number}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-zinc-500">{t('policyStartDate')}</span>
                    <span className="text-sm font-mono">{formatDateDMY(viewingInsurance.policy_start_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-zinc-500">{t('policyEndDate')}</span>
                    <span className="text-sm font-mono">{formatDateDMY(viewingInsurance.policy_end_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-zinc-500">{t('coveragePeriod')}</span>
                    <span className="text-sm font-mono">{viewingInsurance.coverage_period_months} {t('months')}</span>
                  </div>
                </div>

                {/* Contact Info */}
                {(viewingInsurance.company_phone || viewingInsurance.company_email) && (
                  <div className="bg-blue-50 border border-blue-200 p-4 space-y-2">
                    <p className="text-xs font-semibold text-blue-700 uppercase">{t('contactInfo')}</p>
                    {viewingInsurance.company_phone && (
                      <a href={`tel:${viewingInsurance.company_phone}`} className="flex items-center gap-2 text-sm text-blue-700 hover:underline" data-testid="detail-phone">
                        <Phone size={14} />{viewingInsurance.company_phone}
                      </a>
                    )}
                    {viewingInsurance.company_email && (
                      <a href={`mailto:${viewingInsurance.company_email}`} className="flex items-center gap-2 text-sm text-blue-700 hover:underline" data-testid="detail-email">
                        <EnvelopeSimple size={14} />{viewingInsurance.company_email}
                      </a>
                    )}
                    {viewingInsurance.contact_person && (
                      <div className="flex items-center gap-2 text-xs text-blue-600"><User size={14} />{viewingInsurance.contact_person}</div>
                    )}
                  </div>
                )}

                {viewingInsurance.notes && (
                  <div className="text-sm text-zinc-600 border-t border-zinc-200 pt-3">{viewingInsurance.notes}</div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => {
                      setInsuranceDetailDialogOpen(false);
                      openInsuranceDialog(selectedBuilding, viewingInsurance);
                    }}
                    variant="outline"
                    className="flex-1 rounded-none"
                    data-testid="edit-insurance-from-detail"
                  >
                    <PencilSimple size={16} className="mr-2" />{t('edit')}
                  </Button>
                  <Button
                    onClick={() => {
                      setInsuranceDetailDialogOpen(false);
                      openInsuranceHistoryDialog(selectedBuilding);
                    }}
                    variant="outline"
                    className="flex-1 rounded-none"
                    data-testid="view-history-from-detail"
                  >
                    <ClockCounterClockwise size={16} className="mr-2" />{t('viewHistory')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Insurance History Dialog */}
        <Dialog open={insuranceHistoryDialogOpen} onOpenChange={setInsuranceHistoryDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('insuranceHistory')} - {selectedBuilding?.name}
              </DialogTitle>
            </DialogHeader>
            {insuranceList.length === 0 ? (
              <div className="p-8 text-center text-zinc-400">{t('noInsurance')}</div>
            ) : (
              <div className="space-y-3">
                {insuranceList.map((ins, idx) => (
                  <div key={ins.insurance_id} className={`border p-4 ${idx === 0 ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200'}`} data-testid={`insurance-history-${ins.insurance_id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium">{ins.company_name}</div>
                        {ins.policy_number && <div className="text-xs font-mono text-zinc-400">#{ins.policy_number}</div>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs font-mono uppercase border ${getInsuranceStatusBadge(ins.status)}`}>
                          {t(ins.status)}
                        </span>
                        {idx === 0 && <span className="text-xs bg-zinc-900 text-white px-2 py-0.5">{t('latest')}</span>}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm text-zinc-600">
                      <div>
                        <span className="text-xs text-zinc-400">{t('policyStartDate')}</span>
                        <div className="font-mono">{formatDateDMY(ins.policy_start_date)}</div>
                      </div>
                      <div>
                        <span className="text-xs text-zinc-400">{t('policyEndDate')}</span>
                        <div className="font-mono">{formatDateDMY(ins.policy_end_date)}</div>
                      </div>
                      <div>
                        <span className="text-xs text-zinc-400">{t('daysLeft')}</span>
                        <div className={`font-mono ${ins.days_until_expiry < 0 ? 'text-red-600' : ins.days_until_expiry <= 30 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {ins.days_until_expiry} {t('days')}
                        </div>
                      </div>
                    </div>
                    {ins.notes && <div className="text-xs text-zinc-500 mt-2 border-t border-zinc-100 pt-2">{ins.notes}</div>}
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="outline" size="sm" className="rounded-none"
                        onClick={() => { setInsuranceHistoryDialogOpen(false); openInsuranceDialog(selectedBuilding, ins); }}
                        data-testid={`edit-insurance-history-${ins.insurance_id}`}
                      >
                        <PencilSimple size={14} className="mr-1" />{t('edit')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Phase 214 — Delete Confirmation. Three logical paths:
              1. Permanent delete (`deleteMode === 'permanent'`): only
                 reachable from an archived building. Strong red warning,
                 lists every collection that will be wiped.
              2. Soft delete with data: explains "moved to Archive" and
                 lists what will be preserved. Action label says "Archive".
              3. Soft delete, empty: simple confirmation. Action label is
                 just "Delete" because nothing is preserved. */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={(o) => { if (!o) { setDeleteDialogOpen(false); setDeleteSummary(null); setDeleteMode('soft'); } }}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {deleteMode === 'permanent'
                  ? (t('permanentlyDeleteBuilding') || 'Permanently Delete Building')
                  : t('deleteBuilding')}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  {deleteSummary === null ? (
                    <p className="text-zinc-500 text-sm">{t('loading')}…</p>
                  ) : deleteMode === 'permanent' ? (
                    <>
                      <p className="text-red-700 font-semibold text-sm" data-testid="permanent-delete-warning">
                        {t('permanentDeleteWarning') || 'This action is irreversible. The building and ALL related data will be permanently removed from the database. This cannot be undone.'}
                      </p>
                      {deleteSummary.has_data && (
                        <div className="bg-red-50 border border-red-200 p-2.5 text-xs space-y-0.5 font-mono" data-testid="delete-summary-counts">
                          {Object.entries(deleteSummary.counts || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                            <div key={k} className="flex justify-between">
                              <span className="text-zinc-600">{k.replace(/_/g, ' ')}</span>
                              <span className="font-bold text-red-700">{v}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-[11px] text-zinc-500">
                        {t('adminAuthRequired') || 'Admin authorization is required for this action.'}
                      </p>
                    </>
                  ) : deleteSummary.has_data ? (
                    <>
                      <p className="text-zinc-700 text-sm" data-testid="archive-warning">
                        {t('willBeArchivedWarning') || 'This building has data attached and will be moved to Archive. All data is preserved and the building can be restored at any time. Permanent deletion (admin-only) is available from the Archive view.'}
                      </p>
                      <div className="bg-amber-50 border border-amber-200 p-2.5 text-xs space-y-0.5 font-mono" data-testid="delete-summary-counts">
                        {Object.entries(deleteSummary.counts || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                          <div key={k} className="flex justify-between">
                            <span className="text-zinc-600">{k.replace(/_/g, ' ')}</span>
                            <span className="font-bold text-amber-700">{v}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-zinc-700 text-sm" data-testid="empty-delete-warning">
                      {t('emptyBuildingDeleteWarning') || 'This building has no data attached. It will be permanently removed.'}
                    </p>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-btn">
                {deleteMode === 'permanent'
                  ? (t('permanentlyDelete') || 'Permanently Delete')
                  : (deleteSummary?.has_data ? (t('archive') || 'Archive') : t('delete'))}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== UNPAID DETAIL MODAL ====== */}
        <Dialog open={unpaidModal.open} onOpenChange={(open) => { if (!open) setUnpaidModal(prev => ({ ...prev, open: false })); }}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="unpaid-detail-modal">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {unpaidModal.buildingName} — {unpaidModal.groupLabel}
              </DialogTitle>
            </DialogHeader>
            {unpaidLoading ? (
              <div className="py-12 text-center text-zinc-500">{t('loading')}</div>
            ) : unpaidDetails && unpaidDetails.items.length > 0 ? (
              <>
                <table className="w-full text-sm" data-testid="unpaid-detail-table">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left px-3 py-2 text-xs font-semibold text-zinc-500">{t('description')}</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-zinc-500">{t('vendor')}</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-zinc-500">{t('amount')}</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-zinc-500">{t('remaining')}</th>
                      <th className="text-center px-3 py-2 text-xs font-semibold text-zinc-500">{t('status')}</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-zinc-500">{t('expenseDate')}</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-zinc-500">{t('daysOverdue')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {unpaidDetails.items.map((item, idx) => (
                      <tr key={item.expense_id} className="hover:bg-zinc-50" data-testid={`unpaid-item-${idx}`}>
                        <td className="px-3 py-2.5">
                          <p className="font-medium text-sm">{item.category_name}</p>
                          {item.description && <p className="text-xs text-zinc-400 truncate max-w-xs">{item.description}</p>}
                        </td>
                        <td className="px-3 py-2.5 text-zinc-600 text-sm">{item.vendor_name || '—'}</td>
                        <td className="px-3 py-2.5 text-right font-mono">&euro;{item.amount.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-right font-mono font-semibold text-red-600">&euro;{item.remaining.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 border ${item.status === 'partially_paid' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                            {item.status === 'partially_paid' ? t('partiallyPaid') : t('unpaid')}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs text-zinc-500">{formatDateDMY(item.date)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-xs">
                          {item.days_overdue > 0 ? (
                            <span className="text-red-500 font-semibold">{item.days_overdue}</span>
                          ) : (
                            <span className="text-zinc-300">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-3 pt-4 border-t border-zinc-200 mt-2">
                  <span className="text-sm font-semibold text-zinc-700">{t('total')}</span>
                  <span className="text-lg font-bold font-mono text-red-600" data-testid="unpaid-modal-total">&euro;{unpaidDetails.total.toFixed(2)}</span>
                </div>
              </>
            ) : (
              <div className="py-8 text-center text-emerald-600 text-sm">{t('noUnpaidItems')}</div>
            )}
            <DialogFooter>
              {unpaidDetails && unpaidDetails.items.length > 0 && (
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={() => { setUnpaidModal(prev => ({ ...prev, open: false })); navigate(`/buildings/${unpaidModal.buildingId}?tab=expenses`); }}
                  data-testid="unpaid-view-all"
                >
                  {t('viewAll')}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </Layout>
  );
}
